import classNames from 'classnames';
import { Link, graphql, useStaticQuery } from 'gatsby';
import React, { FC } from 'react';
import { MdOutlineErrorOutline } from 'react-icons/md';

import {
  useGetPlayerOverviewPageLink,
  useGetPlayerRgRiskAssessmentOverviewPageLink,
} from '@/bits/links/useLink';
import {
  Card,
  CardBody,
  CardCloseButton,
  CardOptions,
  CardOptionsButton,
  ControlledModal,
  InlineIconButton,
  Table,
} from '@/components';
import { useDataGrid } from '@/components/DataGrid';
import {
  DocumentDownloadIcon,
  EyeIcon,
  NextIcon,
  PreviousIcon,
  RefreshIcon,
} from '@/components/icons';
import { useTranslate } from '@/contexts';
import {
  BrandEnum,
  RgCraV2ReviewStatus,
  RgCraV2RiskAggregationOrderBy,
  RgCraV2RiskLevel,
} from '@/globalTypes';
import { getRgRiskLevelColor } from '@/utils';
import formatDate from '@/utils/formatter/formatDate';
import { splitStringIntoTwoWords } from '@/utils/split-string-in-two-words';
import useRgRiskAssessment from './useRgRiskAssessment';
import useRgRiskAssessmentReport from './useRgRiskAssessmentReport';

const BLOCK_STATIC_QUERY = graphql`
  query SanityRgRiskAssessmentBlockStatic {
    sanityRgRiskAssessmentBlock {
      title {
        ...SanityLocaleString
      }
      playerGlobalId {
        ...SanityLocaleString
      }
      brands {
        ...SanityLocaleString
      }
      risk {
        ...SanityLocaleString
      }
      calculatedAt {
        ...SanityLocaleString
      }
      reviewStatus {
        ...SanityLocaleString
      }
      lastReviewedDate {
        ...SanityLocaleString
      }
    }
  }
`;

const RiskAssessmentBlock: FC = () => {
  const { t } = useTranslate();
  const getPlayerOverviewLink = useGetPlayerOverviewPageLink();
  const getRgRiskAssessmentOverviewPage =
    useGetPlayerRgRiskAssessmentOverviewPageLink();

  const report = useRgRiskAssessmentReport();

  const block =
    useStaticQuery<Queries.SanityRgRiskAssessmentBlockStaticQuery>(
      BLOCK_STATIC_QUERY,
    ).sanityRgRiskAssessmentBlock;

  const {
    entries,
    fetching,
    nextPage,
    previousPage,
    refresh,
    orderBy,
    desc,
    setOrderBy,
    defaultFilter,
    updateFilter,
    isLegacy,
  } = useRgRiskAssessment();

  const { DataGrid, GlobalCardOptions } = useDataGrid({
    name: 'rgRiskAssessments',
    data: entries,
    fetching,
    orderBy,
    desc,
    setOrderBy,
    defaultFilter,
    updateFilter,
    schema: (s) => [
      s.stringValue({
        field: 'playerGlobalId',
        title: t(block?.playerGlobalId),
        filterField: 'playerGlobalId',
        linkTo: ({ row }) =>
          (row?.playerGlobalId &&
            getRgRiskAssessmentOverviewPage(row.playerGlobalId)) ??
          '',
      }),
      s.enumValue({
        field: 'brand',
        title: t(block?.brands),
        e: BrandEnum,
        wrap: ({ row }) => {
          const players = row.players ?? [];
          return (
            <div className="flex items-center">
              {players.length < 3 ? (
                <span>
                  {players.map((player) => player.brand.name).join(', ')}
                </span>
              ) : (
                <>
                  <span className="text-blue-600">{players.length}</span>
                  <ControlledModal
                    content={
                      <Card
                        title={t(block?.brands)}
                        size="lg"
                        options={
                          <CardOptions>
                            <CardCloseButton />
                          </CardOptions>
                        }
                      >
                        <div className="p-3">
                          <Table
                            headings={['Player ID', 'Brand']}
                            rows={players.map((player) => ({
                              key: player.id,
                              data: [
                                <Link
                                  className="w-full h-full p-3 block"
                                  to={getPlayerOverviewLink(player.id) ?? ''}
                                >
                                  {player.rawPlayerId}
                                </Link>,
                                player.brand.name,
                              ],
                            }))}
                          />
                        </div>
                      </Card>
                    }
                  >
                    <InlineIconButton>
                      <EyeIcon className="text-gray-400 text-lg" />
                    </InlineIconButton>
                  </ControlledModal>
                </>
              )}
            </div>
          );
        },
      }),
      s.enumValue({
        field: 'rgCraRiskLevels',
        title: t(block?.risk),
        filterField: 'rgCraRiskLevels',
        isMulti: true,
        e: RgCraV2RiskLevel,
        orderBy: RgCraV2RiskAggregationOrderBy.riskLevel,
        wrap: ({ row }) => (
          <span>
            <MdOutlineErrorOutline
              className={classNames(
                'inline-block',
                'mr-2',
                'text-lg',
                getRgRiskLevelColor(row.riskLevel),
              )}
            />
            {splitStringIntoTwoWords(row.riskLevel)}
          </span>
        ),
      }),
      ...(!isLegacy
        ? [
            s.numberValue({
              field: 'score',
              title: 'Score',
              hidden: true,
            }),
            s.numberValue({
              field: 'maxScore',
              title: 'Max Score',
              hidden: true,
            }),
            s.numberValue({
              field: 'percentage',
              title: 'Percentage',
              fromFilterField: 'percentageFrom',
              toFilterField: 'percentageTo',
              maxToFilter: 100,
              hidden: true,
            }),
            s.numberValue({
              field: 'finalScore',
              title: 'Final Score',
            }),
            s.stringValue({
              field: 'rgCraRiskTermRiskId',
              title: 'Risk ID',
              filterField: 'rgCraRiskTermRiskId',
              hidden: true,
            }),
            s.enumValue({
              field: 'rgCraRiskTermRiskLevels',
              title: 'Risk Term Level',
              filterField: 'rgCraRiskTermRiskLevels',
              e: RgCraV2RiskLevel,
              isMulti: true,
              hidden: true,
            }),
          ]
        : []),
      s.dateTimeValue({
        field: 'calculatedAt',
        title: t(block?.calculatedAt),
        fromFilterField: 'from',
        toFilterField: 'to',
        orderBy: RgCraV2RiskAggregationOrderBy.calculatedAt,
        includeTime: true,
        includeTimeInFilter: true,
      }),
      s.enumValue({
        field: 'reviewStatus',
        title: t(block?.reviewStatus),
        filterField: 'rgCraReviewStatus',
        isMulti: true,
        e: RgCraV2ReviewStatus,
        orderBy: RgCraV2RiskAggregationOrderBy.reviewStatus,
        getValue: ({ row }) => row.reviewStatusInfo?.status,
      }),
      ...(!isLegacy
        ? [
            s.enumValue({
              field: 'overriddenRiskLevel',
              title: 'Overridden Risk',
              e: RgCraV2RiskLevel,
              wrap: ({ row }) => {
                const overriddenRiskLevel = row.overriddenRiskLevel;
                return (
                  <span>
                    {overriddenRiskLevel ? (
                      <>
                        <MdOutlineErrorOutline
                          className={classNames(
                            'inline-block',
                            'mr-2',
                            'text-lg',
                            getRgRiskLevelColor(overriddenRiskLevel),
                          )}
                        />
                        {splitStringIntoTwoWords(overriddenRiskLevel)}
                      </>
                    ) : (
                      '-'
                    )}
                  </span>
                );
              },
            }),
          ]
        : []),
      s.dateTimeValue({
        field: 'lastReviewedDate',
        title: t(block?.lastReviewedDate),
        fromFilterField: 'lastReviewedFrom',
        toFilterField: 'lastReviewedTo',
        orderBy: !isLegacy
          ? RgCraV2RiskAggregationOrderBy.lastReviewedAt
          : undefined,
        getValue: ({ row }) => formatDate(row.reviewStatusInfo?.lastReviewed),
      }),
    ],
  });

  if (!block) {
    return null;
  }

  return (
    <Card
      size="lg"
      title={<div className="flex items-center gap-3">{t(block.title)}</div>}
      showOptionsAtBottom
      options={
        <CardOptions>
          <CardOptionsButton
            disabled={!previousPage}
            onClick={() => previousPage && previousPage()}
          >
            <PreviousIcon />
          </CardOptionsButton>
          <CardOptionsButton
            disabled={!nextPage}
            onClick={() => nextPage && nextPage()}
          >
            <NextIcon />
          </CardOptionsButton>
          <GlobalCardOptions />
          <CardOptionsButton
            onClick={() => refresh({ requestPolicy: 'network-only' })}
          >
            <RefreshIcon />
          </CardOptionsButton>
          <CardOptionsButton
            disabled={report.fetching}
            onClick={() => report.generate()}
          >
            <DocumentDownloadIcon />
          </CardOptionsButton>
        </CardOptions>
      }
    >
      <CardBody>
        <DataGrid />
      </CardBody>
    </Card>
  );
};

export default RiskAssessmentBlock;
